import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Gauge, ArrowRight, AlertTriangle, CheckCircle2 } from "lucide-react";

/**
 * ReadinessGauge — surfaces a user's aggregate ClaimReady score across matters.
 * Modes:
 *  - "full":    Dashboard hero band (ring + label + focus matter + weakest categories)
 *  - "compact": Header pill (small ring + number + link to focus matter)
 * Fetches /api/readiness once and caches in state; callers can pass `data`
 * directly to skip the fetch (e.g. Dashboard already has cases loaded).
 */
const LEVEL = {
  not_started: { fg: "#94A3B8", bg: "#F1F5F9", ring: "#CBD5E1" },
  needs_info:  { fg: "#B45309", bg: "#FEF3C7", ring: "#F59E0B" },
  in_progress: { fg: "#1D4ED8", bg: "#DBEAFE", ring: "#2563EB" },
  organized:   { fg: "#0F766E", bg: "#CCFBF1", ring: "#14B8A6" },
  complete:    { fg: "#166534", bg: "#DCFCE7", ring: "#16A34A" },
};

function Ring({ pct, color, size = 96, stroke = 8, showValue = true, label }) {
  const r = size / 2 - stroke;
  const c = 2 * Math.PI * r;
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg viewBox={`0 0 ${size} ${size}`} className="w-full h-full -rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} strokeWidth={stroke} fill="none" stroke="#E2E8F0" />
        <circle
          cx={size / 2} cy={size / 2} r={r}
          strokeWidth={stroke} fill="none" stroke={color}
          strokeDasharray={c}
          strokeDashoffset={c * (1 - Math.max(0, Math.min(1, pct / 100)))}
          strokeLinecap="round"
        />
      </svg>
      {showValue && (
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="font-serif-display font-semibold text-slate-900 leading-none" style={{ fontSize: size * 0.32 }}>
            {pct}
          </div>
          {label && (
            <div className="text-[9px] font-mono uppercase tracking-widest mt-1" style={{ color }}>
              {label}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ReadinessGauge({ mode = "full" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const r = await api.get("/readiness");
        if (active) setData(r.data);
      } catch {
        if (active) setData({ matter_count: 0, matters: [], aggregate_total: 0, aggregate_level: { label: "Not Started", color: "not_started", range: [0, 24] } });
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  if (loading) return null;
  if (!data || data.matter_count === 0) {
    // Nothing to show yet — first-time user
    if (mode === "compact") return null;
    return null;
  }

  const level = LEVEL[data.aggregate_level?.color] || LEVEL.not_started;

  if (mode === "compact") {
    const focus = data.focus_matter;
    return (
      <Link
        to={focus ? `/case/${focus.case_id}` : "/dashboard"}
        className="inline-flex items-center gap-2 pl-1 pr-3 py-1 border border-slate-300 hover:bg-slate-50 rounded-full transition-colors"
        data-testid="readiness-gauge-compact"
        aria-label={`ClaimReady average ${data.aggregate_total}, ${data.aggregate_level.label}`}
      >
        <Ring pct={data.aggregate_total} color={level.ring} size={28} stroke={4} showValue={false} />
        <span className="tabular-nums font-semibold text-sm text-slate-900">{data.aggregate_total}</span>
        <span className="hidden sm:inline text-[10px] font-mono uppercase tracking-widest" style={{ color: level.fg }}>
          {data.aggregate_level.label}
        </span>
      </Link>
    );
  }

  return (
    <section
      className="mb-8 border rounded-2xl p-6 md:p-8"
      style={{ background: "#FFFFFF", borderColor: "#E2E8F0", boxShadow: "0 1px 2px rgba(15,23,42,0.04)" }}
      data-testid="readiness-gauge"
    >
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 items-center">
        {/* Big ring */}
        <div className="md:col-span-3 flex md:justify-start items-center gap-4">
          <Ring pct={data.aggregate_total} color={level.ring} size={128} stroke={10} label="Avg" />
          <div className="md:hidden">
            <div className="label-tiny">Overall readiness</div>
            <div className="font-serif-display text-2xl leading-tight" style={{ color: level.fg }}>
              {data.aggregate_level.label}
            </div>
          </div>
        </div>

        {/* Summary + focus */}
        <div className="md:col-span-5">
          <div className="hidden md:block label-tiny">Overall readiness</div>
          <div className="hidden md:block font-serif-display text-3xl leading-tight" style={{ color: level.fg }}>
            {data.aggregate_level.label}
          </div>
          <p className="text-slate-600 text-sm md:text-[15px] mt-2 leading-relaxed">
            You have <span className="font-semibold text-slate-900">{data.matter_count} matter{data.matter_count === 1 ? "" : "s"}</span> in progress.
            Focus on the matter with the biggest gap first — it&apos;s where a small improvement moves your readiness the most.
          </p>
          {data.focus_matter && (
            <Link
              to={`/case/${data.focus_matter.case_id}`}
              className="mt-4 inline-flex items-center gap-2 group"
              data-testid="readiness-focus-link"
            >
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded font-mono text-[10px] tracking-widest uppercase" style={{ background: level.bg, color: level.fg }}>
                <AlertTriangle className="w-3 h-3" /> Focus
              </span>
              <span className="font-medium text-slate-900 underline decoration-slate-300 group-hover:decoration-slate-900 underline-offset-4">
                {data.focus_matter.name}
              </span>
              <span className="tabular-nums text-slate-500 text-sm">· {data.focus_matter.total}</span>
              <ArrowRight className="w-4 h-4 text-slate-500 group-hover:translate-x-0.5 transition-transform" />
            </Link>
          )}
        </div>

        {/* Weakest categories on focus matter */}
        <div className="md:col-span-4">
          <div className="label-tiny mb-2">Next to complete</div>
          {data.focus_matter?.weakest?.length ? (
            <ul className="space-y-2">
              {data.focus_matter.weakest.map((w) => (
                <li key={w.name} className="flex items-center gap-3 text-sm" data-testid={`readiness-weakest-${w.name.toLowerCase().replace(/\s+/g,"-")}`}>
                  <span className="flex-1 truncate text-slate-800">{w.name}</span>
                  <div className="w-24 h-1.5 rounded relative overflow-hidden bg-slate-200">
                    <div
                      className="absolute inset-y-0 left-0"
                      style={{ width: `${Math.max(0, Math.min(100, w.percent || 0))}%`, background: level.ring }}
                    />
                  </div>
                  <span className="tabular-nums text-slate-500 text-xs w-8 text-right">{Math.round(w.percent || 0)}%</span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-sm text-slate-500 flex items-center gap-1.5">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" /> Nothing pressing — good place to run a ClaimReady Report.
            </div>
          )}
        </div>
      </div>

      {/* All matters strip */}
      {data.matters.length > 1 && (
        <div className="mt-6 pt-5 border-t border-slate-200">
          <div className="label-tiny mb-3">All matters</div>
          <div className="flex flex-wrap gap-2" data-testid="readiness-matters-strip">
            {data.matters.map((m) => {
              const l = LEVEL[m.level.color] || LEVEL.not_started;
              return (
                <Link
                  key={m.case_id}
                  to={`/case/${m.case_id}`}
                  className="inline-flex items-center gap-2 pl-1.5 pr-3 py-1 border rounded-full hover:bg-slate-50 transition-colors max-w-[280px]"
                  style={{ borderColor: "#E2E8F0" }}
                  data-testid={`readiness-matter-${m.case_id}`}
                >
                  <Ring pct={m.total} color={l.ring} size={22} stroke={3} showValue={false} />
                  <span className="text-sm truncate text-slate-800">{m.name}</span>
                  <span className="tabular-nums text-xs text-slate-500 shrink-0">{m.total}</span>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-4 flex items-center gap-2 text-xs text-slate-500">
        <Gauge className="w-3.5 h-3.5" />
        Powered by the deterministic ClaimReady formula. Open a matter to see per-category detail.
      </div>
    </section>
  );
}
